import type { IKSlice, IKState, ViewerSliceCreator } from "./types";

export const ikInitialState: IKState = {
  ikEnabled: false,
  ikGizmos: [],
  ikPositionMask: [true, true, true],
  ikRotationMask: [false, false, false],
  hiddenGizmoIds: {},
};

export const createIKSlice: ViewerSliceCreator<IKSlice> = (set) => ({
  ...ikInitialState,

  setIKEnabled: (enabled) =>
    set((state) => {
      state.ikEnabled = enabled;
    }),

  addIKGizmos: (msg) =>
    set((state) => {
      state.ikGizmos = msg.gizmos;
      state.ikPositionMask = msg.position_mask;
      state.ikRotationMask = msg.rotation_mask;
    }),

  removeIKGizmos: () =>
    set((state) => {
      state.ikGizmos = [];
      state.hiddenGizmoIds = {};
    }),

  updateIKResult: (msg) =>
    set((state) => {
      if (msg.success) {
        // Update mesh transforms
        for (const update of msg.mesh_transforms) {
          const mesh = state.meshes[update.uuid];
          if (mesh) {
            mesh.position = update.position;
            mesh.quaternion = update.quaternion;
          }
        }
        // Update gizmo transforms - create new array to ensure React detects changes
        if (msg.gizmo_transforms.length > 0) {
          // Build a map for O(1) lookup
          const updateMap = new Map(
            msg.gizmo_transforms.map(u => [u.uuid, u])
          );
          // Create a new array with updated gizmos
          state.ikGizmos = state.ikGizmos.map((gizmo) => {
            const update = updateMap.get(gizmo.gizmo_id);
            if (update) {
              return {
                ...gizmo,
                position: update.position,
                quaternion: update.quaternion,
              };
            }
            return gizmo;
          });
        }
        // Update joint values (sync sliders) - use index Map for O(1) lookup
        if (msg.joint_values && msg.joint_values.length > 0) {
          const jointIndex = new Map(
            state.joints.map((j, i) => [j.name, i])
          );
          for (const jv of msg.joint_values) {
            const idx = jointIndex.get(jv.name);
            if (idx !== undefined) {
              state.joints[idx].current_angle = jv.value;
            }
          }
        }
      }
    }),

  updateIKGizmoPosition: (gizmoId, position, quaternion) =>
    set((state) => {
      const gizmo = state.ikGizmos.find(g => g.gizmo_id === gizmoId);
      if (gizmo) {
        gizmo.position = position;
        gizmo.quaternion = quaternion;
      }
    }),

  setIKPositionMask: (mask) =>
    set((state) => {
      state.ikPositionMask = mask;
    }),

  setIKRotationMask: (mask) =>
    set((state) => {
      state.ikRotationMask = mask;
    }),

  toggleGizmoVisibility: (gizmoId) =>
    set((state) => {
      if (state.hiddenGizmoIds[gizmoId]) {
        delete state.hiddenGizmoIds[gizmoId];
      } else {
        state.hiddenGizmoIds[gizmoId] = true;
      }
    }),
});
